/**
 * Netflix-style Film Card Component
 */

import { useState } from '@wordpress/element';

export default function FilmCard({ film, showActions = true, onTrailerClick, isNowPlaying = false }) {
    const t = window.rialtoData?.translations || {};

    const getRatingClass = (rating) => {
        if (!rating) return '';
        const num = parseFloat(rating);
        if (num >= 7) return 'high';
        if (num >= 5) return 'medium';
        return 'low';
    };

    const formatRating = (rating) => {
        if (!rating) return null;
        return parseFloat(rating).toFixed(1);
    };

    const formatDuration = (mins) => {
        if (!mins || mins <= 0) return null;
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        if (h > 0 && m > 0) return `${h}h ${m}m`;
        if (h > 0) return `${h}h`;
        return `${m}m`;
    };

    const handleTrailerClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onTrailerClick && (film.trailer_embed || film.trailer_url)) {
            onTrailerClick(film);
        }
    };

    const hasTrailer = film.trailer_embed || film.trailer_url;

    return (
        <div className="rialto-card">
            <a href={film.url} className="rialto-card-link">
                <div className="rialto-card-poster">
                    {/* Now Playing overlay badge */}
                    {isNowPlaying && (
                        <div className="rialto-now-playing-label">NOW PLAYING</div>
                    )}

                    {film.poster || film.poster_thumb ? (
                        <img
                            src={film.poster || film.poster_thumb}
                            alt={film.title}
                            loading="lazy"
                        />
                    ) : (
                        <div className="rialto-card-placeholder">
                            <span>{film.title}</span>
                        </div>
                    )}

                    {/* Rating Badge */}
                    {film.rating && (
                        <div className={`rialto-card-rating ${getRatingClass(film.rating)}`}>
                            <svg viewBox="0 0 24 24">
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                            <span>{formatRating(film.rating)}</span>
                        </div>
                    )}

                    {/* HD/CC Badges */}
                    {(film.hd_flag || film.closed_captions) && (
                        <div className="rialto-card-badges">
                            {film.hd_flag && <span className="rialto-card-badge">HD</span>}
                            {film.closed_captions && <span className="rialto-card-badge">CC</span>}
                        </div>
                    )}

                    {/* Hover Info */}
                    <div className="rialto-card-info">
                        <h3 className="rialto-card-title">{film.title}</h3>
                        <div className="rialto-card-meta">
                            {film.year && <span>{film.year}</span>}
                            {film.duration > 0 && (
                                <>
                                    <span className="rialto-card-meta-sep"></span>
                                    <span>{formatDuration(film.duration)}</span>
                                </>
                            )}
                            {film.certification && (
                                <>
                                    <span className="rialto-card-meta-sep"></span>
                                    <span>{film.certification}</span>
                                </>
                            )}
                        </div>

                        {showActions && (
                            <div className="rialto-card-actions">
                                {hasTrailer && (
                                    <button
                                        className="rialto-card-action play"
                                        onClick={handleTrailerClick}
                                        title={t.watchTrailer || 'Watch Trailer'}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                            <polygon points="5 3 19 12 5 21 5 3" />
                                        </svg>
                                    </button>
                                )}
                                <button className="rialto-card-action" title={t.moreInfo || 'More Info'}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="6 9 12 15 18 9" />
                                    </svg>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </a>
        </div>
    );
}
